import { ReactNode, useMemo, useRef, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  useV2GetAccountQuery,
  useV2GetProjectByIdQuery,
  useV2ListProjectLocationsQuery,
  useV2OfferExternalRequestMutation,
  useV2RemoveCrmIntegrationFromProjectMutation,
  useV2RestartScopingForProjectByIdMutation,
} from "@generated";
import { AdminFormPage } from "@components/CatalogIndex";
import { TextField } from "@components/FormsV2";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import IconTextButton from "@components/Buttons/IconTextButton/IconTextButton";
import CreateBlueprintModal from "@components/CreateBlueprintFromProjectModal/CreateBlueprintFromProjectModal";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import useUnloadWarning from "@utils/customHooks/useUnloadWarning";
import { formatUnprocessibleResponse } from "@utils/helperFunctions";
import { toCountryId } from "@components/ClientRecordPicker/siteLink";
import ProjectForm from "../ProjectForm/ProjectForm";
import useSaveProject from "../ProjectForm/api/useSaveProject";
import { toVariableValues } from "../ProjectForm/formHelpers";
import type {
  ApiRecord,
  NewProjectLocation,
  NewProjectValues,
  ProjectFormInitialValues,
} from "../ProjectForm/types";

const option = (record?: ApiRecord, label = record?.attributes?.name) =>
  record ? { value: String(record.id), label: label ?? "" } : null;

const toProjectLocation = (record: ApiRecord): NewProjectLocation => {
  const a = record.attributes ?? {};
  return {
    name: a.name ?? "",
    street: a.street ?? "",
    street2: a.street2 ?? "",
    city: a.city ?? "",
    state: a.state ?? "",
    postalCode: a["postal-code"] ?? "",
    countryId: toCountryId(a.country) ?? "",
    remote: !!a.remote,
    projectVariables: toVariableValues(a["project-variables"]),
    link: null,
  };
};

/**
 * The saved project, its included records and its locations as the values
 * ProjectForm starts on. Only a single location is edited in the form.
 */
const toProjectFormValues = (
  project: ApiRecord,
  included: ApiRecord[],
  locations: ApiRecord[]
): ProjectFormInitialValues => {
  const find = (type: string) => included.find((i) => i.type === type);
  const a = project.attributes ?? {};
  const client = find("clients");
  const crm = find("crm-opportunities");
  const paymentTerm = find("payment-terms");
  const businessUnit = find("business-units");
  return {
    projectName: a["project-name"] ?? "",
    client: {
      id: client?.id ? String(client.id) : null,
      name: client?.attributes?.name ?? "",
    },
    crmOpportunity: option(
      crm,
      crm?.attributes?.["display-name"] || crm?.attributes?.name
    ),
    salesExecutive: option(find("sales-executives")),
    presalesEngineer: option(find("presales-engineers")),
    paymentTermId: paymentTerm?.id ? String(paymentTerm.id) : null,
    rateTable: option(find("rate-tables")),
    businessUnitId: businessUnit?.id ? String(businessUnit.id) : null,
    msaDate: a["msa-date"] ?? null,
    tags: a["tag-list"] ?? [],
    projectVariables: toVariableValues(a["project-variables"]),
    location: locations.length === 1 ? toProjectLocation(locations[0]) : null,
  };
};

const FORM_ID = "edit-project-form";
// How long a status change's message shows before the page reloads.
const RELOAD_DELAY_MS = 2000;

type Toast = { variant: "success" | "warning" | "danger"; text: string };

const EditProjectV2 = (): JSX.Element => {
  const { project_id: projectId = "" } = useParams();
  const { accessLevel } = usePrivilegeCheck();

  const project = useV2GetProjectByIdQuery({
    id: projectId,
    include: [
      "client",
      "sales-executive",
      "presales-engineer",
      "payment-term",
      "rate-table",
      "business-unit",
      "crm-opportunity",
      "external-request",
    ],
  });
  const locations = useV2ListProjectLocationsQuery(
    { filter: { project: projectId } },
    { refetchOnMountOrArgChange: true }
  );
  const account = useV2GetAccountQuery({});
  const { update } = useSaveProject();
  const [removeCrm] = useV2RemoveCrmIntegrationFromProjectMutation();
  const [restartScoping] = useV2RestartScopingForProjectByIdMutation();
  const [offerServices] = useV2OfferExternalRequestMutation();

  const [submitting, setSubmitting] = useState(false);
  // Guards a second submit in the same tick, before `submitting` re-renders.
  const inFlight = useRef(false);
  const [dirty, setDirty] = useState(false);
  // Bumped after a save so the form starts over on the saved values. Other
  // refetches (Remove CRM) keep what the user has typed.
  const [formVersion, setFormVersion] = useState(0);
  const [toast, setToast] = useState<Toast | null>(null);
  const [modal, setModal] = useState<
    "rescope" | "blueprint" | "removeCrm" | null
  >(null);
  useUnloadWarning(dirty);

  const projectRecord = project.data?.data as ApiRecord | undefined;
  const included = (project.data?.included ?? []) as ApiRecord[];
  const locationRecords = (locations.data?.data ?? []) as ApiRecord[];
  const initialValues = useMemo(
    () =>
      projectRecord &&
      toProjectFormValues(projectRecord, included, locationRecords),
    [project.data, locations.data]
  );

  if (project.isLoading || locations.isLoading) return <ScopeStackSpinner />;
  if (!projectRecord || !initialValues) {
    return (
      <ToastAlert
        variant="danger"
        text="The project could not be loaded."
        onClose={() => null}
      />
    );
  }

  const fieldLabels = (account.data?.data?.attributes as any)?.["field-labels"];
  const status: string = projectRecord.attributes?.status ?? "";
  const canEdit = ["manage", "purge"].includes(
    accessLevel("projects.overview") ?? ""
  );
  const canSave = canEdit && status === "building";
  const wonOrApproved = status === "won" || status === "approved";
  const externalRequest = included.find((i) => i.type === "external-requests");
  const showOfferServices =
    (wonOrApproved || status === "lost") &&
    ["accepted", "change_request"].includes(
      externalRequest?.attributes?.status
    );
  const showCreateBlueprint =
    wonOrApproved &&
    ["manage", "create"].includes(accessLevel("settings.blueprints") ?? "");
  const rescopeLabel = fieldLabels?.rescope || "Rescope";
  const locationCount = locationRecords.length;

  const reloadAfter = (text: string) => {
    setToast({ variant: "success", text });
    setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
  };

  const handleSubmit = async (values: NewProjectValues) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setToast(null);
    try {
      const { warning } = await update(
        projectId,
        // Several locations are edited on their own page.
        locationCount > 1 ? { ...values, location: null } : values,
        locationCount === 1 ? String(locationRecords[0].id) : null
      );
      setDirty(false);
      await Promise.all([project.refetch(), locations.refetch()]);
      setFormVersion((v) => v + 1);
      setToast(
        warning
          ? { variant: "warning", text: warning }
          : { variant: "success", text: "Project changes saved" }
      );
    } catch (err) {
      setToast({
        variant: "danger",
        text:
          err instanceof Error
            ? err.message
            : "The project could not be saved.",
      });
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const handleRemoveCrm = () => {
    setModal(null);
    removeCrm({ id: projectId })
      .unwrap()
      .then(() => {
        setToast({ variant: "success", text: "CRM Opportunity removed" });
        project.refetch();
      })
      .catch((err) =>
        setToast({
          variant: "danger",
          text: formatUnprocessibleResponse(
            { response: err },
            "CRM Integration"
          ),
        })
      );
  };

  const handleRescope = () => {
    setModal(null);
    restartScoping({ id: projectId })
      .unwrap()
      .then(() => reloadAfter("The Project has been resubmitted for scoping"))
      .catch(() =>
        setToast({
          variant: "danger",
          text: "Something went wrong rescoping the project.",
        })
      );
  };

  const handleOfferServices = () => {
    if (!externalRequest?.id) return;
    const clientName =
      included.find((i) => i.type === "clients")?.attributes?.name ||
      "the client";
    offerServices({ id: String(externalRequest.id) })
      .unwrap()
      .then(() => reloadAfter(`The Project has been offered to ${clientName}`))
      .catch((err) =>
        setToast({
          variant: "danger",
          text: formatUnprocessibleResponse({ response: err }, "Project"),
        })
      );
  };

  const linkedCrm = initialValues.crmOpportunity;
  // A linked opportunity is shown, not picked; Remove unlinks it.
  const renderCrm = (field: ReactNode, crmLabel: string) =>
    linkedCrm ? (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span className="form-label" style={{ marginBottom: 0 }}>
            {crmLabel}
          </span>
          {canSave && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setModal("removeCrm");
              }}
              style={{
                color: "#f15825",
                fontSize: "0.875rem",
                whiteSpace: "nowrap",
              }}
            >
              <i
                className="fa fa-chain-broken"
                style={{ marginRight: "4px" }}
              />
              Remove
            </a>
          )}
        </div>
        <TextField
          label={null}
          id="crmOpportunity"
          value={linkedCrm.label}
          onChange={() => null}
          readOnly
          disabled
        />
      </>
    ) : (
      field
    );

  // Two or more locations are managed on their own page.
  const renderLocation = (fields: ReactNode) => (
    <>
      {locationCount < 2 && fields}
      {canSave && locationCount > 0 && (
        <Row>
          {locationCount === 1 && (
            <Col>
              <IconTextButton
                url={`/projects/${projectId}/project_locations/new`}
                dataMethod={null}
                className="addLocation"
                iconClass="fa fa-plus"
                buttonText="Add Service Location"
                uniqueStyles={null}
              />
            </Col>
          )}
          <Col>
            <IconTextButton
              url={`/projects/${projectId}/project_locations`}
              dataMethod={null}
              className="addLocation"
              iconClass="fa fa-edit"
              buttonText="Manage Service Locations"
              uniqueStyles={null}
            />
          </Col>
        </Row>
      )}
    </>
  );

  return (
    <AdminFormPage
      title="Project Overview"
      formId={canSave ? FORM_ID : undefined}
      saveDisabled={submitting}
      extraActions={
        <>
          {wonOrApproved && (
            <Button className="btnSeawhite" onClick={() => setModal("rescope")}>
              {rescopeLabel}
            </Button>
          )}
          {showCreateBlueprint && (
            <Button
              className="btnSeawhite"
              onClick={() => setModal("blueprint")}
            >
              Create Blueprint
            </Button>
          )}
          {showOfferServices && (
            <Button className="btnSeawhite" onClick={handleOfferServices}>
              Offer Services
            </Button>
          )}
        </>
      }
    >
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
      <ProjectForm
        key={formVersion}
        id={FORM_ID}
        isSubmitting={submitting}
        onSubmit={handleSubmit}
        onEdit={() => setDirty(true)}
        initialValues={initialValues}
        readOnly={!canSave}
        renderCrm={renderCrm}
        renderLocation={renderLocation}
      />

      <ScopeStackModal
        show={modal === "removeCrm"}
        modalTitle="Remove CRM Opportunity"
        modalBody="Do you want to remove this CRM Opportunity from the project?"
        button1Text="Cancel"
        button2Text="Remove"
        handleClose={() => setModal(null)}
        handleButton1Click={() => setModal(null)}
        handleButton2Click={handleRemoveCrm}
        deleteModal
      />
      <ScopeStackModal
        show={modal === "rescope"}
        modalTitle={`${rescopeLabel} Project`}
        modalBody="Are you sure you want to rescope this project? It will be resubmitted for scoping."
        button1Text="Cancel"
        button2Text={rescopeLabel}
        handleClose={() => setModal(null)}
        handleButton1Click={() => setModal(null)}
        handleButton2Click={handleRescope}
      />
      <CreateBlueprintModal
        projectId={projectId}
        show={modal === "blueprint"}
        onClose={() => setModal(null)}
      />
    </AdminFormPage>
  );
};

export default EditProjectV2;
